import {Container, Graphics, Renderer, Sprite, Texture} from "pixi.js";
import PixiShapes from "external-services/pixijs-utils/shapes";
import {
  CHAIR_BORDER_RADIUS,
  CHAIR_H,
  CHAIR_W,
  DESIGN_SCALE,
} from "floorplan-v2/app/components/Tables/Chair/types";
import {BasicShapes, EllipseOffset} from "external-services/pixijs-utils/types";
import {
  GREEN1,
  GREY1,
  SLOT_H,
  SLOT_W,
  STROKE_W1,
  STROKE_W2
} from "floorplan-v2/app/components/Tables/TableBookingSlots/types";
import {ZOOM_100} from "floorplan-v2/app/services/AppScaleService/types";
import TableAngleService from "floorplan-v2/app/services/TableAngle";
import {FloorplanColors} from "floorplan-v2/app/services/floorplanStatusService/types";

const NS = 'FloorplanSpriteService';

/**
 * Docs: https://nowbookit.atlassian.net/wiki/spaces/ND/pages/2628255748/PixiJS+Sprites+and+reusing+Textures
 */

class FloorplanSpriteService {

  private renderer: Renderer;
  private appScale: number = ZOOM_100 * window.devicePixelRatio;

  private tableShapes = new Map<string, Texture>();
  private chairShapes = new Map<string, Texture>();
  private tableSlotShapes = new Map<string, Texture>();
  private tableSlotDottedLines = new Map<string, Texture>();
  private tablePlus = new Map<string, Texture>();
  private customerNameBg = new Map<string, Texture>();

  setRenderer(value: Renderer) {
    this.renderer = value;
  }

  clearTextures() {

    this.tableShapes.forEach(texture => {
      texture.destroy(true);
    });

    this.chairShapes.forEach(texture => {
      texture.destroy(true);
    });
    this.tableSlotShapes.forEach(texture => {
      texture.destroy(true);
    });

    this.tableSlotDottedLines.forEach(texture => {
      texture.destroy(true);
    });

    this.tablePlus.forEach(texture => {
      texture.destroy(true);
    });
    this.customerNameBg.forEach(texture => {
      texture.destroy(true);
    });
    this.customerNameBg.clear();
    this.tableShapes.clear();
    this.chairShapes.clear();
    this.tableSlotShapes.clear();
    this.tableSlotDottedLines.clear();
    this.tablePlus.clear();
  }

  getTablePlus(clr: number) {

    if (!this.renderer) {
      console.warn(NS, 'You must call "getTablePlus" before "getTableSlotDottedLines".');
      return null;
    }
    const width = 14 * DESIGN_SCALE;

    const key = this.getTablePlusKey(clr);

    const cachedTexture = this.tablePlus.get(key);
    if (cachedTexture) {
      return this.getSprite(cachedTexture, key, width, width);
    }

    const plusGraphic = this.getPlus(clr, width);

    const texture = this.renderer.generateTexture(plusGraphic);
    plusGraphic.destroy(true);

    this.tablePlus.set(key, texture);

    return this.getSprite(texture, key, width, width);
  }

  getTableSlotDottedLines(clr: number, i: number) {

    if (!this.renderer) {
      console.warn(NS, 'You must call "setRenderer" before "getTableSlotDottedLines".');
      return null;
    }
    const scaledW = Math.round(SLOT_W * ZOOM_100);
    const scaledH = Math.round(SLOT_H * ZOOM_100);

    const key = this.getTableSlotDottedLinesKey(clr);

    const cachedTexture = this.tableSlotDottedLines.get(key);
    if (cachedTexture) {
      return this.getSprite(cachedTexture, key, SLOT_W, SLOT_H);
    }

    const strokeW = Math.round(STROKE_W1 * ZOOM_100);
    const dottedOutlineGraphic = PixiShapes.drawDotLineShape(0, 0, scaledW, scaledH, strokeW, clr, BasicShapes.rect,
      EllipseOffset.toEdgeCeil,
      `${NS}-${i}-dottedOutline`);

    const texture = this.renderer.generateTexture(dottedOutlineGraphic);
    dottedOutlineGraphic.destroy(true);

    this.tableSlotDottedLines.set(key, texture);
    return this.getSprite(texture, key, SLOT_W, SLOT_H);
  }

  getTableBg(isRect: boolean, w: number, h: number, clr: number, strokeW: number, strokeClr: string | number): Sprite {

    if (!this.renderer) {
      console.warn(NS, 'You must call "setRenderer" before "getTableBg".');
      return null;
    }
    const scaledW = w * this.appScale;
    const scaledH = h * this.appScale;

    const key = this.getTableKey(isRect, w, h, clr, strokeClr);

    const cachedTexture = this.tableShapes.get(key);
    if (cachedTexture) {
      return this.getSprite(cachedTexture, key, w, h);
    }

    const tableGraphic: Graphics = PixiShapes.drawShape(
      0, 0, scaledW, scaledH, clr,
      isRect ? BasicShapes.roundedRect : BasicShapes.ellipse,
      EllipseOffset.toEdgeCeil,
      `table-bg`
    );
    tableGraphic.stroke({
      width: strokeW * this.appScale,
      color: strokeClr,
    });

    const texture = this.renderer.generateTexture(tableGraphic);
    tableGraphic.destroy(true);

    this.tableShapes.set(key, texture);
    return this.getSprite(texture, key, w, h);
  }

  getCustomerNameBg(w: number, h: number, clr: number): Sprite {

    if (!this.renderer) {
      console.warn(NS, 'You must call "setRenderer" before "getTableBg".');
      return null;
    }
    const key = this.getCustomerNameBgKey(w, h, clr);

    const cachedTexture = this.customerNameBg.get(key);
    if (cachedTexture) {
      return this.getSprite(cachedTexture, key, w, h);
    }

    const background = new Graphics();
    background.roundRect(0, 0, w, h, 4)
    background.fill(0xBAEEFF);

    const texture = this.renderer.generateTexture(background);
    background.destroy(true);

    this.customerNameBg.set(key, texture);
    return this.getSprite(texture, key, w, h);
  }


  getChair(clr : number, tableAngle: number, chairAngle: number) {

    if (!this.renderer) {
      console.warn(NS, 'You must call "setRenderer" before "getChair".');
      return null;
    }

    const isTilted = TableAngleService.isTilted(tableAngle + chairAngle);

    // if chair is tilted (not 90° angle) and using a dark colour the sprites looked aliased, so we just use graphic
    const noSprite = isTilted && (
      clr === FloorplanColors.Seat.Primary
    );

    const scaledW = noSprite ? CHAIR_W : CHAIR_W * this.appScale;
    const scaledH = noSprite ? CHAIR_H : CHAIR_H * this.appScale;
    const pivotX = scaledW /2;
    const pivotY = scaledH /2;

    const key = this.getChairKey(clr, isTilted);


    if (!noSprite) {
      const cachedTexture = this.chairShapes.get(key);
      if (cachedTexture) {
        const sprite = this.getSprite(cachedTexture, key, CHAIR_W, CHAIR_H);
        return {chair: sprite, pivotX, pivotY};
      }
    }

    const rad = CHAIR_BORDER_RADIUS * this.appScale;
    const chairGraphic = PixiShapes.drawRectRounded({
      x: 0, y: 0,
      w: scaledW,
      h: scaledH
    }, {
      topLeft: rad, topRight: rad, botLeft: 0, botRight: 0
    }, clr);

    if (noSprite) {
      chairGraphic.label = key;
      return {chair: chairGraphic, pivotX, pivotY};
    }

    const newTexture = this.renderer.generateTexture(chairGraphic);
    chairGraphic.destroy(true);

    this.chairShapes.set(key, newTexture);
    const sprite = this.getSprite(newTexture, key, CHAIR_W, CHAIR_H);
    return {chair: sprite, pivotX, pivotY};
  }


  getTableSlot(clr : number, slotIndex: number, strokeClr: string | number, includeStrokeBuffer = false): Sprite {

    if (!this.renderer) {
      console.warn(NS, 'You must call "setRenderer" before "getTableSlot".');
      return null;
    }

    const key = this.getTableSlotKey(clr, strokeClr, includeStrokeBuffer);

    const cachedTexture = this.tableSlotShapes.get(key);
    if (cachedTexture) {
      return this.getSprite(cachedTexture, key);
    }

    // includeStrokeBuffer will add an extra pixel to each edge to cover strokes
    const tableSlotGraphic: Graphics = PixiShapes.drawShape(
      includeStrokeBuffer ? -1 : 0,
      includeStrokeBuffer ? -1 : 0,
      includeStrokeBuffer ? SLOT_W + 2 : SLOT_W,
      includeStrokeBuffer ? SLOT_H + 2 : SLOT_H,
      clr,
      BasicShapes.rect,
      EllipseOffset.toEdgeCeil,
      `${NS}-${slotIndex}-bg`
    );

    if (strokeClr) {
      tableSlotGraphic.stroke({
        width: STROKE_W2,
        color: strokeClr,
      });
    }

    // tableSlotGraphic.roundPixels = true;

    const texture = this.renderer.generateTexture(tableSlotGraphic);
    tableSlotGraphic.destroy(true);

    this.tableSlotShapes.set(key, texture);
    return this.getSprite(texture, key);
  }

  private roundTo2Dec(num: number): number {
    return Math.round(num * 100) / 100;
  }

  private getCustomerNameBgKey(w: number, h: number, clr: number): string {
    return `customerNameBg-${this.roundTo2Dec(w)}-${this.roundTo2Dec(h)}-${clr.toString()}`;
  }

  private getTableKey(isRect: boolean, w: number, h: number, clr: number, strokeClr: string | number): string {
    return `table-${isRect ? 'rect' : 'circ'}-${this.roundTo2Dec(w)}-${this.roundTo2Dec(h)}-${clr.toString()}-${strokeClr.toString()}`;
  }

  private getChairKey(clr: number, isTilted: boolean): string {
    return `chair-${clr.toString()}-${isTilted ? 'tilted' : 'straight'}`;
  }

  private getTableSlotKey(clr: number, strokeClr: string | number, includeStrokeBuffer: boolean): string {
    return `tableSlot-${clr.toString()}-${strokeClr ? strokeClr.toString() : 'nostroke'}-${includeStrokeBuffer ? 'buf' : 'nobuf'}`;
  }

  private getTableSlotDottedLinesKey(clr: number): string {
    return `tableSlotDottedLines-${clr.toString()}`;
  }

  private getTablePlusKey(clr: number): string {
    return `tablePlus-${clr.toString()}`;
  }


  private getPlus(clr: number, width: number) {

    const plusShape = new Graphics();

    const thickness = 2 * DESIGN_SCALE;
    const scaledWidth = Math.round(width * this.appScale);
    const scaledThickness = Math.round(thickness * this.appScale);
    const scaledEdge = Math.round(((width - thickness) / 2) * this.appScale);

    const plusLineVert = new Graphics();
    plusLineVert.rect(scaledEdge, 0, scaledThickness, scaledWidth);
    plusLineVert.fill(clr);

    const plusLineHoriz = new Graphics();
    plusLineHoriz.rect(0, scaledEdge, scaledWidth, scaledThickness);
    plusLineHoriz.fill(clr);

    plusShape.addChild(plusLineVert);
    plusShape.addChild(plusLineHoriz);

    return plusShape;
  }

  private getSprite(texture: Texture, label: string, w?: number, h?: number): Sprite {
    const sprite = new Sprite(texture);
    sprite.roundPixels = true;
    sprite.label = label;

    if (w) {
      sprite.width = w;
    }
    if (h) {
      sprite.height = h;
    }
    return sprite;
  }
}

const instance = new FloorplanSpriteService();
export default instance as FloorplanSpriteService;
